import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  App,
  Form,
  Input,
  Button,
  Typography,
  Divider,
  Alert,
  Space,
  Checkbox,
} from "antd";
import {
  LockOutlined,
  LoginOutlined,
  MailOutlined,
} from "@ant-design/icons";

import { useTranslation } from "react-i18next";

import "./login.scss";

import GoogleLogin from "../../../Components/GoogleLogin/googlelogin";
import FacebookLogin from "../../../Components/FacebookLogin/facebooklogin";

import {
  login,
  resetPassword as resetPassswordFunc,
  resetPasswordRequest,
  getUserDetails,
} from "../../../services/authService";
import {
  saveCredentials,
  getSavedCredentials,
  clearSavedCredentials,
} from "../../../services/localStorage";
import { ROUTE_IDS } from "../../../i18n/routeIds";
import { PRODUCT_IDS } from "../../../i18n/productContext";
import { useDashboardNavigate } from "../../../i18n/hooks/useDashboardNavigate";
import { useProductContext } from "../../../i18n/providers/ProductContextProvider";
import {
  resolveLoginPostLoginTarget,
  traceLoginLocaleFlow,
} from "../../../i18n/loginLocaleFlow";
import { useAppContext } from "../../../contexts/AppContext";
import { useLocalePath } from "../../../contexts/LocalePathContext";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../../utils/hotelsRiverBrandLogo";
import { getMonthlyLoginBackground } from "../../../utils/loginMonthlyBackgrounds";

const { Title, Text } = Typography;
const LOGIN_2FA_PENDING_KEY = "hr_login_2fa_pending";

function Login({ resetPasssword = false }) {
  const { notification } = App.useApp();
  const { resetToken } = useParams();
  const [form] = Form.useForm();
  const [error, setError] = useState("");
  const [requestingResetPassword, setRequestingResetPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageReady, setImageReady] = useState(false);

  const { appLogin } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dashNav = useDashboardNavigate();
  const { locale, product } = useProductContext();
  const { localizePath } = useLocalePath();
  const [googleData, setGoogleData] = useState("");
  const handleGoogleDataChange = (nextGoogleData) => {
    setGoogleData(nextGoogleData);
  };

  const [rememberMe, setRememberMe] = useState(false);

  const background = useMemo(() => getMonthlyLoginBackground(), []);

  useEffect(() => {
    setImageReady(false);
    if (!background?.url) return undefined;
    const img = new Image();
    img.decoding = "async";
    let cancelled = false;
    img.onload = () => {
      if (!cancelled) setImageReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setImageReady(true);
    };
    img.src = background.url;
    return () => {
      cancelled = true;
    };
  }, [background?.url]);

  useEffect(() => {
    traceLoginLocaleFlow({
      loginPath: typeof window !== "undefined" ? window.location.pathname : "/login",
      resolvedLocale: locale,
      product,
      phase: "mount",
    });
  }, [locale, product]);

  useEffect(() => {
    const savedCreds = getSavedCredentials();
    if (savedCreds && savedCreds.email && savedCreds.password) {
      form.setFieldsValue({
        email: savedCreds.email,
        password: savedCreds.password,
      });
      setRememberMe(true);
    }
  }, [form]);

  const handleSubmit = async (values) => {
    setError("");
    setLoading(true);

    const data = {
      email: values.email,
      password: values.password,
      externalID: "",
      locale,
      product,
    };

    try {
      const res = await login(data);

      if (res && (res.requires_2fa === true || res.status === "requires_2fa")) {
        sessionStorage.setItem(
          LOGIN_2FA_PENDING_KEY,
          JSON.stringify({
            verification_session: res.verification_session,
            masked_email: res.masked_email,
            message: res.message,
            can_resend: res.can_resend,
            resend_available_at: res.resend_available_at,
          })
        );
        navigate(localizePath("/login/verify"));
      } else if (res && res.status === "ok") {
        if (rememberMe) {
          saveCredentials(values.email, values.password);
        } else {
          clearSavedCredentials();
        }

        const user = getUserDetails();
        if (user) {
          appLogin(user);
          const userType = user.usertype;

          if (userType === "clients") {
            const target = resolveLoginPostLoginTarget(userType, locale);
            traceLoginLocaleFlow({
              loginPath: window.location.pathname,
              resolvedLocale: locale,
              product,
              postLoginTarget: target.path,
              postLoginRouteId: target.routeId,
              phase: "post_login",
            });
            dashNav.to(ROUTE_IDS.SECURE_CLIENT_ROOT, {
              product: PRODUCT_IDS.AGENT,
              locale,
            });
          } else if (userType === "administrators") {
            const target = resolveLoginPostLoginTarget(userType, locale);
            traceLoginLocaleFlow({
              loginPath: window.location.pathname,
              resolvedLocale: locale,
              product,
              postLoginTarget: target.path,
              postLoginRouteId: target.routeId,
              phase: "post_login",
            });
            dashNav.to(ROUTE_IDS.ADMINISTRATION, {
              product: PRODUCT_IDS.ADMIN,
              locale,
            });
          } else {
            const target = resolveLoginPostLoginTarget(userType, locale);
            traceLoginLocaleFlow({
              loginPath: window.location.pathname,
              resolvedLocale: locale,
              product,
              postLoginTarget: target.path,
              postLoginRouteId: target.routeId,
              phase: "post_login",
            });
            dashNav.to(target.routeId, {
              product: PRODUCT_IDS.AGENT,
              locale,
            });
          }
        } else {
          setError(t("login.error2") || "שגיאה בטעינת נתוני המשתמש");
        }
      } else {
        const errorMessage = res?.message || t("login.error2") || "שגיאה בהתחברות";
        setError(errorMessage);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(t("login.error2") || "שגיאה בהתחברות");
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    const email = form.getFieldValue("email");

    if (!email) {
      setError(t("login.error1"));
      return;
    }

    setRequestingResetPassword(true);
    try {
      const res = await resetPasswordRequest(email);
      if (res.status === "success") {
        notification.success({
          message: t("login.resetPasswordRequestSuccessTitle"),
          description: t("login.resetPasswordRequestSuccessMessage"),
        });
        navigate(`/`);
      } else {
        notification.error({
          message: t("login.resetPasswordRequestFailedTitle"),
          description: t("login.resetPasswordRequestFailedMessage"),
        });
      }
    } catch (err) {
      console.error(err);
      setError(t("login.error2"));
    } finally {
      setRequestingResetPassword(false);
    }
  };

  const handleSubmitReset = async (values) => {
    if (!resetToken) {
      setError(t("login.error1"));
      return;
    }

    setRequestingResetPassword(true);
    setError("");

    try {
      const res = await resetPassswordFunc(values.password, resetToken);
      if (res.status === "success") {
        notification.success({
          message: t("login.passwordChangeSuccessTitle"),
          description: t("login.passwordChangeSuccessMessage"),
        });
        navigate(dashNav.pathFor(ROUTE_IDS.LOGIN) || "/login");
      } else {
        notification.error({
          message: t("login.resetPasswordFailedTitle"),
          description: t("login.resetPasswordFailedMessage"),
        });
      }
    } catch (err) {
      console.error(err);
      setError(t("login.error2"));
    } finally {
      setRequestingResetPassword(false);
    }
  };

  return (
    <div className="login-page" data-testid="agent-login-page">
      <div
        className="login-page__bg"
        role="img"
        aria-label={`${background.monthNameHe}: ${background.labelHe}`}
      >
        <div
          className={[
            "login-page__bg-image",
            imageReady ? "is-visible" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ backgroundImage: `url("${background.url}")` }}
          data-bg-id={background.id}
        />
        <div className="login-page__bg-wash" aria-hidden />
      </div>

      <div className="login-page__stage">
        <div className="login-page__brand">
          <img
            className="login-page__logo"
            src={HOTELSRIVER_OFFICIAL_LOGO_URL}
            alt="HotelsRiver"
          />
          <p className="login-page__tagline">{t("login.tagline")}</p>
        </div>

        <main className="login-page__card">
          <div className="login-page__form-head">
            <Title level={2}>
              {resetPasssword
                ? t("login.password-reset-title")
                : t("login.title")}
            </Title>
            {!resetPasssword && (
              <Text className="login-page__welcome">
                {t("login.welcomeBack") ||
                  "Welcome back! Please login to your account"}
              </Text>
            )}
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              closable
              showIcon
              onClose={() => setError("")}
              style={{ marginBottom: 20, borderRadius: 8 }}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={resetPasssword ? handleSubmitReset : handleSubmit}
            autoComplete="on"
            size="large"
          >
            {resetPasssword ? (
              <>
                <Form.Item
                  name="password"
                  label={t("login.newPasswordLabel")}
                  rules={[
                    {
                      required: true,
                      message: t("login.error1"),
                    },
                    {
                      min: 6,
                      message: t("login.passwordMinLength"),
                    },
                  ]}
                >
                  <Input.Password
                    prefix={
                      <LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    placeholder={t("login.newPasswordLabel")}
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<LockOutlined />}
                    loading={requestingResetPassword}
                    block
                    className="login-page__submit"
                  >
                    {t("login.password-reset-title")}
                  </Button>
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  name="email"
                  label={t("login.emailLabel")}
                  rules={[
                    {
                      required: true,
                      message: t("login.error1"),
                    },
                    {
                      type: "email",
                      message: t("login.validEmail"),
                    },
                  ]}
                >
                  <Input
                    prefix={
                      <MailOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    placeholder={t("login.emailLabel")}
                    autoComplete="email"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={t("login.passwordLabel")}
                  rules={[
                    {
                      required: true,
                      message: t("login.error1"),
                    },
                  ]}
                >
                  <Input.Password
                    prefix={
                      <LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />
                    }
                    placeholder={t("login.passwordLabel")}
                    autoComplete="current-password"
                  />
                </Form.Item>

                <div className="login-page__actions-row">
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  >
                    {t("login.rememberMe")}
                  </Checkbox>
                  <Button
                    type="link"
                    onClick={forgotPassword}
                    loading={requestingResetPassword}
                    className="login-page__forgot"
                  >
                    {t("login.requestResetPasswordLinkText")}
                  </Button>
                </div>

                <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<LoginOutlined />}
                    loading={loading}
                    block
                    className="login-page__submit"
                  >
                    {t("login.loginButton")}
                  </Button>
                </Form.Item>

                <p className="login-page__signup">
                  {t("login.noAccountText")}{" "}
                  <Link to={localizePath("/registeragent")}>
                    {t("login.signUpLinkText")}
                  </Link>
                </p>

                <Divider>{t("login.orLoginWithText")}</Divider>

                <div className="login-page__social">
                  <Space direction="horizontal" size={12}>
                    <GoogleLogin
                      googleData={googleData}
                      onGoogleDataChange={handleGoogleDataChange}
                    />
                    <FacebookLogin />
                  </Space>
                </div>

                <div className="login-page__terms">
                  <Text type="secondary">
                    {t("login.agreeTermsText")}{" "}
                    <Link to={localizePath("/termsAndConditions")}>
                      {t("login.termsLinkText")}
                    </Link>
                  </Text>
                </div>
              </>
            )}
          </Form>
        </main>
      </div>

      <p className="login-page__credit">
        <span>
          צילום:{" "}
          <a
            href={background.photographerUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {background.photographer}
          </a>
          {" · "}
          <a
            href={background.unsplashUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Unsplash
          </a>
        </span>
        <span className="login-page__credit-license">{background.license}</span>
      </p>
    </div>
  );
}

export default Login;
